import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { login as loginApi, logout as logoutApi, refreshToken as refreshTokenApi } from '../api/userApi.js';
import { setAuthCallbacks } from '../api/client.js';
import { STORAGE_KEYS } from '../constants/storageKeys.js';

// localStorage 只存 user profile（名字、email、role），供 UI 顯示用。
// access token 只存在 React state（記憶體），重整就消失，由 silent refresh 補回。
// refresh token 存在後端設的 httpOnly cookie，JS 完全讀不到。
const STORAGE_KEY = STORAGE_KEYS.AUTH;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // token 同步存一份 ref：client.js 透過 getToken 讀 token。子元件（如 AccountPage）的 effect
  // 可能比父元件把新 getToken 注入 client.js 早一步跑，導致重整後第一個請求抓到舊 token(null) → 403。
  // ref 在 render 階段就同步成最新值，讓 getToken 永遠讀得到當下 token，消除這個 race。
  const tokenRef = useRef(null);
  tokenRef.current = token;

  const saveUserToStorage = useCallback((userData) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ user: userData }));
    } catch { /* 隱私模式下忽略 */ }
  }, []);

  const clearStorage = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // 首次掛載：嘗試 silent refresh 恢復 session
  // 不管 localStorage 有沒有 token（token 不存在 localStorage），
  // 直接打 /api/auth/refresh，瀏覽器自動帶 httpOnly cookie。
  // 成功 → 取得新 access token，session 恢復；失敗 → 需要重新登入。
  useEffect(() => {
    const silentRefresh = async () => {
      try {
        const result = await refreshTokenApi();
        setUser(result.user);
        setToken(result.token);
        saveUserToStorage(result.user);
      } catch {
        // cookie 不存在或已過期，正常未登入狀態
        clearStorage();
      } finally {
        setIsInitializing(false);
      }
    };
    silentRefresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (credentials) => {
    const result = await loginApi(credentials);
    setUser(result.user);
    setToken(result.token);
    saveUserToStorage(result.user);
    return result.user;
  }, [saveUserToStorage]);

  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } catch { /* 後端登出失敗仍強制清除前端狀態 */ }
    setUser(null);
    setToken(null);
    clearStorage();
  }, [clearStorage]);

  // 給 client.js 的 401 自動 retry 用：打 refresh 拿新 access token
  const doRefresh = useCallback(async () => {
    const result = await refreshTokenApi();
    setUser(result.user);
    setToken(result.token);
    saveUserToStorage(result.user);
    return result.token;
  }, [saveUserToStorage]);

  const forceLogout = useCallback(() => {
    setUser(null);
    setToken(null);
    clearStorage();
    window.location.href = '/login';
  }, [clearStorage]);

  const getToken = useCallback(() => tokenRef.current, []);

  useEffect(() => {
    setAuthCallbacks(doRefresh, forceLogout, getToken);
  }, [doRefresh, forceLogout, getToken]);

  // 後端回傳的權限碼清單（master/編輯員等才有；一般會員為空）
  const permissions = user?.permissions ?? [];

  const value = {
    user,
    setUser, // 個人資料更新後同步顯示
    token,
    permissions,
    hasPermission: (code) => permissions.includes(code),
    isAuthenticated: !!user && !!token,
    // 有任一後台權限即視為可進後台（取代原本的 role === 'admin'）
    isAdmin: permissions.length > 0,
    isInitializing,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth 必須在 <AuthProvider> 內使用');
  return ctx;
}
